import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet } from "../api/client";
import { EmptyMessage } from "../components/EmptyMessage";
import { PageHeader } from "../components/PageHeader";
import { formatShortDate, formatTimeParts } from "../lib/format";

export type SurveySubmission = {
  id: string;
  title: string;
  score: number;
  patient_name?: string;
  provider_name?: string;
  patient_id?: string;
  created_at?: string;
  updated_at?: string;
  comment?: string;
  appointment?: {
    patient_name?: string;
    provider_name?: string;
    patient_id?: string;
  };
  survey_questions_in_order_and_their_answers?: SurveyQuestion[];
};

export type SurveyQuestion = {
  id?: string;
  title: string;
  answer_type_description: {
    type: "multiple_choice" | "long_answer" | string;
    choices?: string[];
  };
  answer?: { answer_string?: string };
};

/** Build provider-view questions when the archive payload is flat. */
export function questionsForSubmission(s: SurveySubmission): SurveyQuestion[] {
  if (s.survey_questions_in_order_and_their_answers?.length) {
    return s.survey_questions_in_order_and_their_answers;
  }
  const score = Number(s.score) || 0;
  const rating =
    score >= 5 ? "Excellent" : score >= 4 ? "Good" : score >= 3 ? "Fair" : "Poor";
  const recommend = score >= 4 ? "Yes" : score >= 3 ? "Maybe" : "No";
  return [
    {
      id: `${s.id}_q1`,
      title: "Overall, how was your visit?",
      answer_type_description: {
        type: "multiple_choice",
        choices: ["Poor", "Fair", "Good", "Excellent"],
      },
      answer: { answer_string: rating },
    },
    {
      id: `${s.id}_q2`,
      title: "Would you recommend us to a friend or family member?",
      answer_type_description: {
        type: "multiple_choice",
        choices: ["No", "Maybe", "Yes"],
      },
      answer: { answer_string: recommend },
    },
    {
      id: `${s.id}_q3`,
      title: "Anything else you'd like to share?",
      answer_type_description: { type: "long_answer" },
      answer: { answer_string: s.comment || "" },
    },
  ];
}

/**
 * Survey submission detail — Angular opens this as
 * `ne__modal--survey-provider`; Vite exposes a first-class route so deep links
 * and VRT can target `/app/surveys/:id`.
 */
export function SurveyDetailPage() {
  const { id } = useParams();
  const [survey, setSurvey] = useState<SurveySubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    apiGet<SurveySubmission>(`/survey_submissions/${id}`)
      .then((d) => {
        if (!cancelled) setSurvey(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || "Failed to load survey");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const questions = useMemo(
    () => (survey ? questionsForSubmission(survey) : []),
    [survey],
  );

  if (loading) {
    return (
      <EmptyMessage>
        <p>Loading survey…</p>
      </EmptyMessage>
    );
  }

  if (error || !survey) {
    return (
      <EmptyMessage title={error || "Survey not found"}>
        <p>
          <Link to="/app/surveys">Back to surveys</Link>
        </p>
      </EmptyMessage>
    );
  }

  const patient =
    survey.appointment?.patient_name || survey.patient_name || "Patient";
  const provider =
    survey.appointment?.provider_name || survey.provider_name || "";
  const when = survey.updated_at || survey.created_at;
  const { time, period } = formatTimeParts(when);

  return (
    <>
      <PageHeader title={survey.title || "Survey"} />
      <div
        className="ne__survey ne__survey--provider-view"
        style={{ padding: "2.5vw 5%" }}
      >
        <p className="ne__survey__meta" style={{ marginBottom: "1.5rem" }}>
          <strong>{patient}</strong>
          {provider ? <> · seen by {provider}</> : null}
          {when ? (
            <>
              {" "}
              · {formatShortDate(when)}
              {time ? (
                <>
                  {" "}
                  at {time}
                  <span className="ne__period"> {period}</span>
                </>
              ) : null}
            </>
          ) : null}
          {" · "}
          <span>Patient rated {survey.score}</span>
        </p>

        <form className="ne__survey__form ne__survey__form--provider">
          {questions.map((question, index) => {
            const type = question.answer_type_description?.type;
            const answer = question.answer?.answer_string || "";
            return (
              <fieldset
                className="ne__fieldset"
                key={question.id || index}
                style={{ marginBottom: "1.5rem" }}
              >
                <header className="ne__fieldset__header">
                  <h5>
                    <b>{index + 1}</b> {question.title}
                  </h5>
                </header>
                {type === "multiple_choice" ? (
                  <ul className="ne__multi-select answers" style={{ listStyle: "none", padding: 0 }}>
                    {(question.answer_type_description.choices || []).map(
                      (option) => (
                        <li className="ne__check-button option" key={option}>
                          <span
                            className={
                              answer === option ? "selected checked" : undefined
                            }
                          >
                            {option}
                            {answer === option ? (
                              <i className="glyphicon glyphicon-ok" />
                            ) : null}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                ) : null}
                {type === "long_answer" ? (
                  <div className="answers">
                    <textarea
                      className="ne__textarea ne__textarea--primary"
                      id={`survey-comment-${question.id || index}`}
                      value={answer}
                      readOnly
                      rows={3}
                      style={{ width: "100%" }}
                    />
                    {answer ? (
                      <span className="ne__placeholder">Patient&apos;s comment</span>
                    ) : null}
                  </div>
                ) : null}
              </fieldset>
            );
          })}
        </form>

        <p style={{ marginTop: "2rem" }}>
          <Link to="/app/surveys">← Back to surveys</Link>
          {survey.patient_id || survey.appointment?.patient_id ? (
            <>
              {" · "}
              <Link
                to={`/app/patients/${survey.patient_id || survey.appointment?.patient_id}/activity`}
              >
                View patient
              </Link>
            </>
          ) : null}
        </p>
      </div>
    </>
  );
}
